import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Alert, Col, Offcanvas, Row } from 'react-bootstrap-5';
import { faArrowDown, faArrowUp, faEllipsisV, faSidebar } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import AttachmentsSection from './components/attachments';
import Button from 'shared/button';
import ClickableIcon from 'components/clickable_icon';
import Contact from 'bundles/crm/associations/contact';
import Contacts from './components/contacts';
import DateInput from 'bundles/apex/dates/input';
import Deal from 'bundles/crm/associations/deal';
import DeleteModal from 'bundles/apex/tasks/delete_modal';
import Dropdown from 'components/dropdown';
import ImmediateRemindersModal from 'bundles/apex/deals/show/critical_dates/modal/immediate_reminders';
import ManagedInput from 'components/form/managed_input';
import Project from 'bundles/crm/associations/project';
import RemindersFields from './components/reminders_fields';
import Section from './components/section';
import Snackbar from 'shared/snackbar';
import Toggle from 'components/toggle';
import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { getParentOptionValue } from 'bundles/crm/parent_helpers';
import { nonClosingProps, useTaskModalState } from './helpers';

import type { Attachment, ForUser, ModalOptions, Task, TaskAttributes } from '../types';
import type { Reminder } from 'bundles/apex/reminders/types';

import 'bundles/apex/tasks/styles.scss';

export interface Props {
  attachmentsPath?: string;
  canDeleteAttachments?: boolean;
  checkImmediateRemindersPath?: string;
  createTask?: (task: TaskAttributes) => Promise<any>;
  currentTask?: Task;
  disableDelete?: boolean;
  formDependenciesPath?: string;
  forUser?: ForUser;
  hasNext?: boolean;
  hasPrevious?: boolean;
  hideAssignee?: boolean;
  options?: ModalOptions;
  pageLoading?: boolean;
  reminders?: Reminder[];
  show: boolean;
  showType?: boolean;
  task?: Task | null,
  updateTask?: (task: TaskAttributes) => Promise<any>;
  onAddAttachment?: (task: Task, attachment: Attachment) => void;
  onDelete?: (task: Task) => void;
  onDeleteAttachment?: (task: Task, attachment: Attachment) => void;
  onHide: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  reloadTasks?: () => void;
}

export default function TaskSidebarModal({
  attachmentsPath,
  canDeleteAttachments,
  checkImmediateRemindersPath,
  createTask,
  currentTask,
  disableDelete,
  formDependenciesPath,
  forUser,
  hasNext,
  hasPrevious,
  hideAssignee,
  options,
  pageLoading,
  reminders,
  show,
  showType,
  task,
  updateTask,
  onAddAttachment,
  onDelete,
  onDeleteAttachment,
  onHide,
  onNext,
  onPrevious,
  reloadTasks
}: Props) {
  const {
    contactsTasks,
    dependencies,
    dependenciesError,
    errors,
    formTask,
    handleAutosave,
    handleChange,
    handleRemindersChange,
    hasChanges,
    hide,
    immediateReminders,
    initializing,
    loading,
    reset,
    requestReminders,
    setContactsTasks,
    setImmediateReminders,
    typeOptions,
    userNames,
  } = useTaskModalState({
    checkImmediateRemindersPath,
    createTask,
    formDependenciesPath,
    forUser,
    hideOnExternalElementClick: true,
    options,
    reminders,
    show,
    updateTask,
    onHide
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disableRequiresAttachment, setDisableRequiresAttachment] = useState(false);
  const [assigneeId, setAssigneeId] = useState(formTask?.assigneeId);
  const [deadline, setDeadline] = useState(formTask?.deadline);
  const [notes, setNotes] = useState(formTask?.notes || '');
  const [parentOptionValue, setParentOptionValue] = useState(formTask?.parentOptionValue);
  const [requiresAttachment, setRequiresAttachment] = useState(formTask?.requiresAttachment?.toString() || 'false');
  const [taskType, setTaskType] = useState(formTask?.taskType || '');
  const [followUpChecked, setFollowUpChecked] = useState(Boolean(formTask?.followUpDate));
  const [followUpDate, setFollowUpDate] = useState(formTask?.followUpDate || '');

  const taskNameRef = useRef<HTMLHeadingElement>(null);
  const { assignableUsers, contacts, deal, fields } = (dependencies || { assignableUsers: [], contacts: [] });
  const handleStatusChange = async (newStatus) => {
    const changes = { completed: newStatus };
    handleChange(changes);
    await handleAutosave(changes, null, true);
    if (newStatus && reloadTasks && followUpDate) {
      setTimeout(() => {
        reloadTasks();
        setFollowUpChecked(false);
        setFollowUpDate('');
      }, 500);
    }
  };
  const handleDateChange = (date) => {
    const newDeadline = typeof date === 'string' ? { ...deadline, date, static: true } : date;
    handleChange({ deadline: newDeadline });
    setDeadline(newDeadline);
    handleAutosave({ deadline: newDeadline }, null, true);
  };
  const handleRemindersChangeAndSave = (reminders, autosave = false) => {
    handleRemindersChange(reminders);
    autosave && handleAutosave({ reminders }, null, true);
  };
  const handleContactsChange = (contactsTasks) => {
    setContactsTasks(contactsTasks);
    handleChange({ contactsTasks });
    handleAutosave({ contactsTasks }, null, true);
  };
  const resetDeadlineAndReminders = () => {
    handleRemindersChange(task?.reminders || []);
    handleChange({ deadline: task?.deadline });
    setDeadline(task?.deadline);
    setImmediateReminders([]);
  };

  const handleFollowUpCheck = () => {
    setFollowUpChecked(!followUpChecked);
    setFollowUpDate('');
    handleAutosave({ followUpDate: '' }, null, true);
  };

  const handleFollowUpChange = (date) => {
    setFollowUpDate(date);
    handleAutosave({ followUpDate: date }, null, true);
  };

  let statusButton;
  if (task?.completed) {
    statusButton = (
      <Button.Success
        className="w-100"
        disabled={!formTask.name}
        icon={faCircleCheck}
        label="Task Completed"
        name="uncomplete"
        onClick={() => handleStatusChange(false)}
      />
    );
  } else {
    const missingAttachment = formTask?.requiresAttachment === 'true' && !formTask.attachments?.length;

    statusButton = (
      <Button.Primary
        className="w-100"
        disabled={!formTask.name || missingAttachment}
        icon={faCircleCheck}
        label="Mark Complete"
        name="complete"
        onClick={() => handleStatusChange(true)}
      />
    );

    if (missingAttachment) {
      statusButton = (
        <Tooltip content="Upload an attachment to complete task" dark placement="top">
          {statusButton}
        </Tooltip>
      );
    }
  }

  useEffect(() => {
    window.onbeforeunload = () => {
      // TODO: This won't work when using the back button to navigate to a different tab on the same page
      // Add deal page solution for this.
      if (show && task && hasChanges) {
        return 'There are unsaved changes. Are you sure you want to leave this page?';
      }
    };
  }, [show && task && hasChanges]);

  useEffect(() => {
    reset(task);
    setAssigneeId(task?.assignee?.id);
    setContactsTasks(task?.contactsTasks || []);
    setDeadline(task?.deadline);
    setNotes(task?.notes || '');
    setParentOptionValue(task ? getParentOptionValue(task) : '');
    setRequiresAttachment(task?.requiresAttachment?.toString() || 'false');
    setTaskType(task?.taskType || '');
    setFollowUpDate(task?.followUpDate || '');
    setFollowUpChecked(Boolean(task?.followUpDate));
  }, [task?.id]);

  useEffect(() => {
    if (!formTask.name && taskNameRef.current && show && !initializing) {
      taskNameRef.current.focus();
    }
  }, [taskNameRef.current]);

  useEffect(() => {
    if (!loading) {
      setDisableRequiresAttachment(!!(task?.id));
    }
  }, [loading]);

  function deleteTask() {
    if (task && onDelete) {
      onDelete(task);
      hide();
    }
  }

  const toggleRequiresAttachment = () => {
    const newRequiresAttachment = requiresAttachment === 'true' ? 'false' : 'true';
    handleChange({ requiresAttachment: newRequiresAttachment });
    setRequiresAttachment(newRequiresAttachment);
    handleAutosave({ requiresAttachment: newRequiresAttachment }, null, true);
  };

  let ParentComponent, parentKey;
  if (dependencies?.parent?.className === 'Apex::Deal') {
    ParentComponent = Deal;
    parentKey = 'deal';
  } else if (dependencies?.parent?.className === 'Backoffice::Contact') {
    ParentComponent = Contact;
    parentKey = 'contact';
  } else if (dependencies?.parent?.className === 'Project') {
    ParentComponent = Project;
    parentKey = 'project';
  }

  let parent;
  if (ParentComponent) {
    const props = { [parentKey]: dependencies?.parent };
    parent = <ParentComponent {...props} target="_blank" />;
  }

  const assigneeOptions: { label: string, value: '' | number }[] = [
    { label: 'None', value: '' },
  ];

  if (currentTask?.assignee?.id && !assignableUsers.find(user => user.id === currentTask.assignee?.id)) {
    assigneeOptions.push({ label: currentTask.assignee.name, value: currentTask.assignee.id });
  }

  assigneeOptions.push(...assignableUsers.map(user => ({ label: user.name, value: user.id })));

  return <>
    <Offcanvas
      {...nonClosingProps}
      backdrop={false}
      className={classNames('task-sidebar-modal', { 'task-sidebar-modal--loading': loading || pageLoading })}
      placement="end"
      show={show && !initializing}
      onHide={hide}
      onShow={() => reset(task)}
    >
      <Offcanvas.Body className="d-flex flex-column gap-4">
        <Row className="flex-column border-bottom">
          <Col className="d-flex flex-row justify-content-between align-items-center mb-4">
            <div className="d-flex">
              <ClickableIcon icon={faSidebar} tooltip="Collapse" onClick={hide} />
            </div>


            <div className="d-flex flex-row align-items-center">
              <ClickableIcon
                disabled={!hasPrevious || !onPrevious}
                icon={faArrowUp}
                tooltip={(!hasPrevious || !onPrevious) ? 'No Previous Item' : undefined}
                onClick={onPrevious}
              />

              <ClickableIcon
                disabled={!hasNext || !onNext}
                icon={faArrowDown}
                tooltip={(!hasNext || !onNext) ? 'No Next Item' : undefined}
                onClick={onNext}
              />

              {task && onDelete && <>
                <Dropdown controlIcon={faEllipsisV}>
                  <Dropdown.MenuItem
                    disabled={disableDelete}
                    disabledMessage="Required tasks cannot be deleted"
                    isDelete
                    onClick={() => setShowDeleteModal(true)}
                  />
                </Dropdown>
                <DeleteModal
                  {...nonClosingProps}
                  show={showDeleteModal}
                  onDelete={deleteTask}
                  onHide={() => setShowDeleteModal(false)}
                />
              </>}
            </div>
          </Col>

          <Col>
            <h6
              className="m-0 py-1 fs-large fw-semibold"
              contentEditable
              placeholder="New Task"
              ref={taskNameRef}
              suppressContentEditableWarning
              onBlur={() => handleAutosave({ name: taskNameRef.current?.innerText.trim() })}
              onInput={e => handleChange(e.currentTarget.innerHTML)}
            >
              {task?.name || ''}
            </h6>
          </Col>
        </Row>

        {parent &&
          <Row className="border-bottom">
            <Col>
              <Section label="Source" vertical>
                {parent}
              </Section>
            </Col>
          </Row>
        }

        <div className="flex-fill d-flex flex-column overflow-hidden">
          <div className="overflow-auto flex-fill">
            {errors && <Alert variant="warning">
              {errors.map((error, i) => (
                <div key={i}>
                  {error}
                </div>
              ))}
            </Alert>}

            {!hideAssignee &&
              <Section label="Assignee">
                <ManagedInput
                  className="task-sidebar-modal__assignee"
                  label="Assigned To"
                  name="assigneeId"
                  options={assigneeOptions}
                  type="reactSelect"
                  value={assigneeId}
                  onBlur={() => handleAutosave({ assigneeId })}
                  onChange={handleChange}
                  onValueChange={setAssigneeId}
                />
              </Section>
            }

            {showType &&
              <div className="d-flex flex-row justify-content-between align-items-center mb-4">
                <h6 className="m-0 fs-large fw-semibold">Type</h6>
                <ManagedInput
                  name="taskType"
                  options={typeOptions || []}
                  type="reactSelect"
                  value={taskType}
                  onBlur={() => handleAutosave({ taskType })}
                  onChange={handleChange}
                  onValueChange={setTaskType}
                />
              </div>
            }

            {options?.parents && options.parents.length > 1 &&
              <Section label="Listing">
                <ManagedInput
                  name="parentOptionValue"
                  options={options.parents}
                  required
                  type="reactSelect"
                  value={parentOptionValue}
                  onBlur={() => handleAutosave({ parentOptionValue })}
                  onChange={handleChange}
                  onValueChange={setParentOptionValue}
                />
              </Section>
            }

            {contacts.length > 0 && (
              <Contacts
                contacts={contacts}
                contactsTasks={contactsTasks}
                onChange={handleContactsChange}
              />
            )}

            <Section label="Notes" vertical>
              <ManagedInput
                className="form-control task-sidebar-modal__notes"
                name="notes"
                type="textarea"
                value={notes}
                onBlur={() => handleAutosave({ notes })}
                onChange={handleChange}
                onValueChange={setNotes}
              />
            </Section>

            <Section label="Due Date">
              <div className={classNames({ 'apex-date-form-group flex-fill ms-2': deal })}>
                {deal
                  ? <>
                    <DateInput
                      className="text-end"
                      deal={deal}
                      fields={fields}
                      name="deadline"
                      popperPlacement="bottom-end"
                      value={deadline}
                      onValueChange={handleDateChange}
                    />
                  </> : (
                    <ManagedInput
                      className="form-control text-end"
                      name="deadline"
                      popperPlacement="bottom-end"
                      required
                      type="date"
                      value={deadline?.date}
                      onValueChange={handleDateChange}
                    />
                  )
                }
              </div>
            </Section>

            <RemindersFields
              deal={deal}
              fields={fields}
              reminders={requestReminders}
              task={formTask}
              onChange={handleRemindersChangeAndSave}
            />
            <ImmediateRemindersModal
              {...nonClosingProps}
              handleSelection={keepReminders => handleAutosave(
                { deadline, reminders: requestReminders }, keepReminders, true
              )}
              show={show && immediateReminders.length > 0}
              userNames={userNames}
              onHide={resetDeadlineAndReminders}
            />

            {!task?.completed && (
              <>
                <Section className="mt-2 mb-0" label="Follow-Up">
                  <ManagedInput
                    checked={followUpChecked}
                    id="follow-up-checkbox"
                    label="Create follow-up"
                    type="checkbox"
                    onChange={handleFollowUpCheck}
                  />
                </Section>
                {followUpChecked && (
                  <Section label="">
                    <div>
                      <ManagedInput
                        className="form-control"
                        label="Follow Up Date"
                        name="follow-up"
                        popperPlacement="top-end"
                        type="date"
                        value={followUpDate}
                        onValueChange={handleFollowUpChange}
                      />
                    </div>
                  </Section>
                )}
              </>
            )}

            <Section className="mt-2 mb-0" label="Attachments">
              <div className="d-flex flex-row align-items-center">
                <small className="me-2">Requires Attachments?</small>
                <Toggle
                  checked={formTask.requiresAttachment === 'true'}
                  disabled={disableRequiresAttachment}
                  name="requiresAttachment"
                  showText={false}
                  small
                  theme="ds"
                  onChange={toggleRequiresAttachment}
                />
              </div>
            </Section>
            {attachmentsPath && forUser && currentTask && onAddAttachment && onDeleteAttachment &&
              <AttachmentsSection
                attachmentsPath={attachmentsPath}
                canDeleteAttachments={canDeleteAttachments}
                forUserId={forUser.id}
                task={currentTask}
                onAddAttachment={onAddAttachment}
                onDeleteAttachment={onDeleteAttachment}
              />}
          </div>
        </div>
      </Offcanvas.Body>
      <div className="offcanvas-footer d-flex flex-column">
        {statusButton}
        {task?.completedBy && task?.completedAt && (
          <small className="mt-2 text-center text-muted">
            Completed by {task.completedBy.name} on {formatDate(task.completedAt)}
          </small>
        )}
      </div>
    </Offcanvas>
    <Snackbar show={dependenciesError} text="Something went wrong" variant="danger" />
  </>;
}
